'use client';

import { useState, useEffect, useRef } from 'react';
import { getConsent, saveConsent, logConsentAction } from '@/lib/consent';

interface Props {
  open: boolean;
  onClose: () => void;
  region: 'gdpr' | 'ccpa' | 'none';
}

export default function ConsentPreferenceCenter({ open, onClose, region }: Props) {
  const [choices, setChoices] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const lastFocusableRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      const existing = getConsent();
      if (existing) {
        setChoices(existing.choices);
      }
      // Focus first element
      setTimeout(() => firstFocusableRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      // Focus trap
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstFocusableRef.current) {
          e.preventDefault();
          lastFocusableRef.current?.focus();
        } else if (!e.shiftKey && document.activeElement === lastFocusableRef.current) {
          e.preventDefault();
          firstFocusableRef.current?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const handleToggle = (category: 'analytics' | 'marketing' | 'preferences') => {
    setChoices((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    saveConsent(choices, region);
    logConsentAction('preferences_saved', { choices });
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    setChoices(allAccepted);
    saveConsent(allAccepted, region);
    logConsentAction('accept_all_from_preferences', { choices: allAccepted });
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preferences-title"
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <h2 id="preferences-title" className="text-xl font-semibold text-gray-900 mb-4">
            Cookie Preferences
          </h2>

          <div className="space-y-4">
            {/* Necessary - Always ON */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Necessary</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Essential for site functionality, security, and authentication. Always enabled.
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked="true"
                  disabled
                  className="ml-4 w-12 h-6 bg-blue-600 rounded-full flex items-center justify-end px-1 cursor-not-allowed"
                >
                  <div className="w-5 h-5 bg-white rounded-full" />
                </button>
              </div>
            </div>

            {/* Analytics */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Analytics</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Help us understand how you use our site to improve your experience.
                  </p>
                </div>
                <button
                  ref={firstFocusableRef}
                  role="switch"
                  aria-checked={choices.analytics}
                  onClick={() => handleToggle('analytics')}
                  className={`ml-4 w-12 h-6 rounded-full flex items-center transition-colors ${
                    choices.analytics ? 'bg-blue-600 justify-end' : 'bg-gray-300 justify-start'
                  }`}
                >
                  <div className="w-5 h-5 bg-white rounded-full" />
                </button>
              </div>
            </div>

            {/* Marketing */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Marketing</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Enable personalized ads and marketing content based on your interests.
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={choices.marketing}
                  onClick={() => handleToggle('marketing')}
                  className={`ml-4 w-12 h-6 rounded-full flex items-center transition-colors ${
                    choices.marketing ? 'bg-blue-600 justify-end' : 'bg-gray-300 justify-start'
                  }`}
                >
                  <div className="w-5 h-5 bg-white rounded-full" />
                </button>
              </div>
            </div>

            {/* Preferences */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Preferences</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Remember your preferences for a personalized experience.
                  </p>
                </div>
                <button
                  role="switch"
                  aria-checked={choices.preferences}
                  onClick={() => handleToggle('preferences')}
                  className={`ml-4 w-12 h-6 rounded-full flex items-center transition-colors ${
                    choices.preferences ? 'bg-blue-600 justify-end' : 'bg-gray-300 justify-start'
                  }`}
                >
                  <div className="w-5 h-5 bg-white rounded-full" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={handleAcceptAll}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Accept All
            </button>
            <button
              ref={lastFocusableRef}
              onClick={handleSave}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              Save Preferences
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
